import React, {
    useRef,
    useEffect,
    useState,
    useMemo,
    PropsWithChildren
} from 'react'
import { Flex, Box, ThemeStyleObject, getCSS, Text } from '../'
import times from 'lodash.times'
import { useThemeUI } from 'theme-ui'

export type StepperVariants = 'raspberry' | 'tiaPink' | 'white' | 'putty'

const variantColors = {
    raspberry: {
        bg: 'raspberry',
        notFilledBorder: 'inputOutlineHover',
        notFilledLine: 'inputOutlineHover'
    },
    tiaPink: {
        bg: 'tiaPink',
        notFilledBorder: 'inputOutlineHover',
        notFilledLine: 'inputOutlineHover'
    },
    white: {
        bg: 'white',
        notFilledBorder: 'inputOutlineHover',
        notFilledLine: 'inputOutlineHover'
    },
    putty: {
        bg: 'putty',
        notFilledBorder: 'inputOutlineHover',
        notFilledLine: 'cream'
    }
}

const Dot = ({
    variant,
    direction,
    stepStatus
}: {
    variant: StepperVariants
    direction: DirectionTypes
    stepStatus: StepTypes
}) => {
    let extraProps

    if (stepStatus === 'completed') {
        extraProps = {
            backgroundColor: variantColors[variant].bg
        }
    } else if (stepStatus === 'active') {
        extraProps = {
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: variantColors[variant].notFilledBorder
        }
    } else {
        extraProps = {
            borderWidth: '2px',
            borderStyle: 'solid',
            borderColor: variantColors[variant].bg
        }
    }

    return (
        <Box
            sx={{
                width: '16px',
                height: '16px',
                borderRadius: '8px',
                marginBottom: direction === 'row' ? '10px' : '16px',
                ...extraProps
            }}
        />
    )
}

type AligmentTypes = 'left' | 'right' | 'center'

type StepProps = {
    sx?: ThemeStyleObject
    variant: StepperVariants
    onMount: (elem: HTMLDivElement) => void
    width: number | null
    stepStatus: StepTypes
    align?: AligmentTypes
    direction?: DirectionTypes
}

const Step = (props: PropsWithChildren<StepProps>) => {
    const {
        children,
        onMount,
        width,
        variant,
        stepStatus,
        align = 'center',
        direction = 'row',
        sx
    } = props
    const ref = useRef<HTMLDivElement>(null)
    const alignItems = useMemo(() => {
        if (align === 'left') {
            return 'flex-start'
        } else if (align === 'right') {
            return 'flex-end'
        }
        return 'center'
    }, [align])

    useEffect(() => {
        if (ref && ref.current) {
            onMount(ref.current)
        }
    }, [children])

    return (
        <Flex
            sx={{
                flexDirection: direction === 'row' ? 'column' : 'row',
                alignItems,
                ...sx
            }}
        >
            <Dot
                variant={variant}
                direction={direction}
                stepStatus={stepStatus}
            />
            <Box
                ref={ref}
                sx={{
                    textAlign: align,
                    variant: 'text.supportingInfo',
                    ...(variant === 'white' ? { color: 'white' } : {}),
                    ...(width ? { width } : {})
                }}
            >
                {children}
            </Box>
        </Flex>
    )
}

export type StepperProps = {
    sx?: ThemeStyleObject
    /**
     * Current active step
     */
    currentStep?: number
    /**
     * An array of strings containing the label for each step
     */
    steps: string[] | { title: string; subtitle?: string }[]
    /**
     * Possible stylings for the component
     */
    variant?: StepperVariants
    /**
     * If true, the first step is aligned to the left and the last one to the right
     */
    boxed?: boolean
    /**
     * If the connecting line between the current step and the next should be blurred
     */
    blurNextStepLine?: boolean
    /**
     * Alignment of stepper
     */
    direction?: DirectionTypes
}

type DirectionTypes = 'row' | 'column'

type FillTypes = 'filled' | 'halfFilled' | 'notFilled'

type StepTypes = 'completed' | 'active' | 'incomplete'

type ConnectingLineProps = {
    sx?: ThemeStyleObject
    variant: StepperVariants
    fill?: FillTypes
}

const ConnectingLine = ({
    sx,
    fill = 'notFilled',
    variant
}: ConnectingLineProps) => {
    const { theme } = useThemeUI()
    let props: ThemeStyleObject = {
        backgroundColor:
            fill === 'filled'
                ? variantColors[variant].bg
                : variantColors[variant].notFilledLine
    }

    if (fill === 'halfFilled') {
        const fromColor = getCSS(theme)('color', variantColors[variant].bg)
        const toColor = getCSS(theme)(
            'color',
            variantColors[variant].notFilledBorder
        )
        props = {
            backgroundImage: `linear-gradient(90deg, ${fromColor} -1.21%, ${toColor} 68.14%)`
        }
    }

    return (
        <Box
            sx={{
                height: '3px',
                borderRadius: '3px',
                ...props,
                ...sx
            }}
        />
    )
}

type ConnectingLinesProps = {
    nSteps: number
    maxWidth: number
    currentStep: number
    variant: StepperVariants
    boxed: boolean
    blurNextStepLine: boolean
}

const ConnectingLines = ({
    nSteps,
    variant,
    currentStep,
    maxWidth,
    boxed,
    blurNextStepLine
}: ConnectingLinesProps) => {
    const fill = (idx: number) => {
        let v: FillTypes = 'notFilled'
        if (idx === currentStep && blurNextStepLine) {
            v = 'halfFilled'
        } else if (idx + 1 <= currentStep) {
            v = 'filled'
        }
        return v
    }
    // margins are being computed to keep distance from the dot
    const getMarginLeft = (lineIdx: number) => {
        if (lineIdx === 0 && boxed) {
            return '24px'
        }
        return lineIdx === 0 ? maxWidth / 2 + 16 : '16px'
    }
    const getMarginRight = (lineIdx: number) => {
        if (lineIdx === nSteps - 2 && boxed) {
            return '24px'
        }
        return lineIdx === nSteps - 2 ? maxWidth / 2 + 16 : '16px'
    }

    return (
        <Flex sx={{ position: 'absolute', width: '100%', top: '7px' }}>
            {times(nSteps - 1, idx => (
                <ConnectingLine
                    key={`step-line-${idx}`}
                    variant={variant}
                    fill={fill(idx)}
                    sx={{
                        flex: 1,
                        ml: getMarginLeft(idx),
                        mr: getMarginRight(idx)
                    }}
                />
            ))}
        </Flex>
    )
}

type VerticalConnectingLinesProps = {
    nSteps: number
    currentStep: number
    variant: StepperVariants
    blurNextStepLine: boolean
}

const VerticalConnectingLines = ({
    nSteps,
    variant,
    currentStep,
    blurNextStepLine
}: VerticalConnectingLinesProps) => {
    const fill = (idx: number) => {
        let v: FillTypes = 'notFilled'
        if (idx === currentStep && blurNextStepLine) {
            v = 'halfFilled'
        } else if (idx + 1 <= currentStep) {
            v = 'filled'
        }
        return v
    }

    return (
        <Flex
            sx={{
                position: 'absolute',
                width: '3px',
                display: 'block',
                ml: '6px'
            }}
        >
            {times(nSteps - 1, idx => (
                <ConnectingLine
                    key={`step-line-${idx}`}
                    variant={variant}
                    fill={fill(idx)}
                    sx={{
                        flex: 1,
                        mt: idx === 0 ? '26px' : '36px',
                        height: '36px'
                    }}
                />
            ))}
        </Flex>
    )
}

export const Stepper = ({
    sx,
    steps,
    currentStep = 0,
    variant = 'raspberry',
    boxed = false,
    blurNextStepLine = false,
    direction = 'row'
}: StepperProps) => {
    const [childSizes, setChildSizes] = useState<Array<null | number>>(
        steps.map(() => null)
    )
    // I compute the max width of all steps (by the text of its label) and later use the same width for all step boxes
    const maxWidth = useMemo(() => {
        if (childSizes.some(childWidth => childWidth === null)) {
            return null
        }
        // @ts-ignore: value is never null because of the previous line
        return childSizes.reduce((a, v) => (v > a ? v : a), 0)
    }, childSizes)
    const getAligment = (stepIdx: number) => {
        let align: AligmentTypes = 'center'
        if (stepIdx === 0 && boxed) {
            align = 'left'
        } else if (stepIdx === steps.length - 1 && boxed) {
            align = 'right'
        }
        return align
    }
    const stepStatus = (idx: number) => {
        let v: StepTypes = 'incomplete'
        if (idx === currentStep) {
            v = 'active'
        } else if (idx + 1 <= currentStep) {
            v = 'completed'
        }
        return v
    }

    return direction === 'row' ? (
        <Box sx={{ ...sx, position: 'relative' }}>
            <Flex sx={{ justifyContent: 'space-between' }}>
                {maxWidth && (
                    <ConnectingLines
                        blurNextStepLine={blurNextStepLine}
                        boxed={boxed}
                        nSteps={steps.length}
                        variant={variant}
                        currentStep={currentStep}
                        maxWidth={maxWidth}
                    />
                )}

                {steps.map((s, idx) => (
                    <Step
                        align={getAligment(idx)}
                        variant={variant}
                        width={maxWidth}
                        key={`step-${idx}`}
                        stepStatus={stepStatus(idx)}
                        onMount={elem => {
                            const { width } = elem.getBoundingClientRect()
                            setChildSizes(oldChildSizes => {
                                const newSizes = [...oldChildSizes]
                                newSizes[idx] = Math.ceil(width)
                                return newSizes
                            })
                        }}
                    >
                        {typeof s === 'object' ? (
                            <Flex
                                sx={{
                                    flexDirection: 'column',
                                    whiteSpace: 'nowrap',
                                    wordBreak: 'keep-all'
                                }}
                            >
                                <Text
                                    as="span"
                                    variant="paragraph"
                                    sx={{ margin: 0 }}
                                >
                                    {s.title}
                                </Text>
                                <Text as="span" variant="buttonSubtext">
                                    {s.subtitle}
                                </Text>
                            </Flex>
                        ) : (
                            s
                        )}
                    </Step>
                ))}
            </Flex>
        </Box>
    ) : (
        <Box sx={{ ...sx, position: 'relative', display: 'flex' }}>
            <Flex sx={{ display: 'flex', flexDirection: 'column' }}>
                <VerticalConnectingLines
                    blurNextStepLine={blurNextStepLine}
                    nSteps={steps.length}
                    variant={variant}
                    currentStep={currentStep}
                />

                {steps.map((s, idx) => {
                    const hasSubtitles = !!(typeof s === 'object' && s.subtitle)
                    return (
                        <Step
                            variant={variant}
                            width={maxWidth}
                            key={`step-${idx}`}
                            stepStatus={stepStatus(idx)}
                            direction={direction}
                            sx={{
                                mb:
                                    idx === steps.length - 1
                                        ? '0px'
                                        : hasSubtitles
                                        ? '31px'
                                        : '42px'
                            }}
                            onMount={elem => {
                                const { width } = elem.getBoundingClientRect()
                                setChildSizes(oldChildSizes => {
                                    const newSizes = [...oldChildSizes]
                                    newSizes[idx] = Math.ceil(width)
                                    return newSizes
                                })
                            }}
                        >
                            <Flex
                                sx={{
                                    flexDirection: 'column',
                                    whiteSpace: 'nowrap',
                                    wordBreak: 'keep-all',
                                    mb: !hasSubtitles ? '10px' : '0px',
                                    ml: 2
                                }}
                            >
                                {typeof s === 'object' ? (
                                    <>
                                        <Text
                                            as="span"
                                            variant="paragraph"
                                            sx={{
                                                margin: 0,
                                                textAlign: 'left'
                                            }}
                                        >
                                            {s.title}
                                        </Text>
                                        <Text
                                            as="span"
                                            variant="buttonSubtext"
                                            sx={{ textAlign: 'left' }}
                                        >
                                            {s.subtitle}
                                        </Text>
                                    </>
                                ) : (
                                    s
                                )}
                            </Flex>
                        </Step>
                    )
                })}
            </Flex>
        </Box>
    )
}
